// @flow

/* eslint-disable func-names */
/* eslint-disable no-multi-assign */
/* eslint-disable no-var */
/* eslint-disable one-var */
/* eslint-env browser */
/* eslint-disable no-underscore-dangle */

import url from 'node:url';

import type { User } from '@gandi/authentication';
import { selectCartWidgetVisible } from '@gandi/cart';
import type { Organization } from '@gandi/redux-sharing';
import type { TranslatorProps } from '@gandi/translate';

import type { GetState } from '../../../../types';

import type { Zopim } from './zopim';

type Args = {
  assetPath: string => string,
  getState: () => any,
  justAcceptedCookies: boolean,
  locale: string,
  personalDetails: ?Organization,
  publicUrls: any,
  sharingDetails: ?Organization,
  translator: TranslatorProps,
  userInfos: ?User,
};

type OnLoadArgs = {
  assetPath: string => string,
  getState: () => any,
  isConnected: boolean,
  justAcceptedCookies: boolean,
  language: string,
  personalDetails: ?Organization,
  publicUrls: any,
  sharingDetails: ?Organization,
  translator: TranslatorProps,
  userInfos: ?User,
};

// zopim getter to cast Zopim
const getZopim = (): Zopim => {
  if (typeof window === 'undefined' || !window.$zopim) {
    throw Error('zopim is not available');
  }
  return window.$zopim;
};

function getName({
  userInfos,
  personalDetails,
}: {
  userInfos: Args['userInfos'],
  personalDetails: Args['personalDetails'],
}) {
  // A minimal orga (freshly created account that never bought anything) will have personalDetails with no firstname/lastname.
  if (
    userInfos &&
    personalDetails &&
    personalDetails.firstname &&
    personalDetails.lastname
  ) {
    return `${userInfos.username} (${personalDetails.firstname} ${personalDetails.lastname})`;
  }

  if (userInfos) {
    return userInfos.username;
  }

  return '';
}

function getNameForNotes({
  userInfos,
  personalDetails,
  sharingDetails,
}: {
  userInfos: Args['userInfos'],
  personalDetails: Args['personalDetails'],
  sharingDetails: Args['sharingDetails'],
}) {
  if (
    userInfos &&
    personalDetails &&
    sharingDetails &&
    sharingDetails.orgname
  ) {
    return `${getName({ userInfos, personalDetails })}  - \n${
      sharingDetails.name
      // $FlowFixMe `sharingDetails.orgname` is checked above
    } (${sharingDetails.orgname})`;
  }

  if (userInfos && personalDetails && sharingDetails) {
    return `${getName({ userInfos, personalDetails })}  - \n${
      sharingDetails.name
    }`;
  }

  return getName({ userInfos, personalDetails });
}

function isCookieAvailable(): boolean {
  return typeof document !== 'undefined';
}

export function isZopimCookieConsented(): boolean {
  if (isCookieAvailable() && document.cookie) {
    const cookies = document.cookie.split(';').reduce((acc, cookie) => {
      // $FlowIgnore[prop-missing]
      acc[cookie.split('=')[0].trim()] = cookie.split('=')[1].trim();
      return acc;
    }, {});

    // search if cookie zopim
    // $FlowIgnore[prop-missing]
    if (cookies.__zlcprivacy === '1') {
      return true;
    }
  }
  return false;
}

export function setCookie() {
  if (isCookieAvailable()) {
    // Strip the subdomain, so the cookie can also be seen on www and shop.
    const host = window.location.hostname.replace(/.+?\./, '');
    document.cookie = `__zlcprivacy=1;path=/;max-age=31536000;samesite=lax;domain=${host}`;
  }
}

/**
 * onConnected is private function
 * used only to setup the livechat
 */
const onConnected = (getState: GetState) => () => {
  getZopim().livechat.setOnStatus((status: 'online' | 'away' | 'offline') => {
    const isChatting = getZopim().livechat.isChatting();
    const cartWidgetClosed = !selectCartWidgetVisible(getState());

    if (cartWidgetClosed && (status === 'online' || !isChatting)) {
      getZopim().livechat.button.show();
    }
  });
};

const windowOnHidePatch = () => {
  // But allow displaying the button so the "All our agents are offline"
  // text is shown; otherwise nothing would be shown at all.
  getZopim().livechat.button.setHideWhenOffline(false);
  getZopim().livechat.button.show();
};

const windowOnShowPatch = () => {
  getZopim().livechat.button.setHideWhenOffline(true);
};

const windowOnShow = () => {
  // Allow showing the window even when offline.
  getZopim().livechat.window.show();

  // Don't actually hide the button when offline;
  // this is a workaround to disable the offlineForm when all support agents
  // are offline.
  //
  // Opening the livechat window will just display an explanation
  // instead of the form allowing to send a message,
  // as they should use the "contact support" form instead.
  window.setTimeout(windowOnShowPatch, 0);
};

const onLoad =
  ({
    assetPath,
    getState,
    isConnected,
    justAcceptedCookies,
    language,
    personalDetails,
    publicUrls,
    sharingDetails,
    translator,
    userInfos,
  }: OnLoadArgs) =>
  () => {
    // connected: we make use of user data
    getZopim().livechat.set({
      email: personalDetails?.email || userInfos?.email || '',
      language,
      name: getName({ userInfos, personalDetails }),
      phone: personalDetails?.phone || '',
    });

    if (isConnected) {
      getZopim().livechat.setNotes(
        getNameForNotes({ userInfos, personalDetails, sharingDetails }),
      );

      if (userInfos) {
        // $FlowFixMe[incompatible-call]
        getZopim().livechat.addTags(...userInfos.roles);
      }
    }

    // Set localization strings.
    getZopim().livechat.setGreetings({
      online: translator.__('Chat with us'),
      offline: translator.__('All our agents are offline'),
    });
    getZopim().livechat.prechatForm.setGreetings(
      translator.__('Ask your question to the support team.'),
    );

    // Set theme and appearance.
    getZopim().livechat.badge.setImage(
      url.resolve(publicUrls.support || '', assetPath('img/chat_button.png')),
    );

    getZopim().livechat.theme.setColors({
      badge: '#11AAAA',
      primary: '#0E8888',
    });

    getZopim().livechat.theme.reload();
    getZopim().livechat.badge.hide();

    const cartWidgetClosed = !selectCartWidgetVisible(getState());

    getZopim().livechat.window.onShow(windowOnShow);

    getZopim().livechat.window.onHide(() => {
      window.setTimeout(windowOnHidePatch, 0);
    });

    getZopim().livechat.button.setHideWhenOffline(false);

    // show the livechat only if the cart widget drawer is not opened
    // to avoid overlapping
    if (cartWidgetClosed) {
      getZopim().livechat.button.show();
    }

    getZopim().livechat.setOnConnected(
      justAcceptedCookies
        ? () => getZopim().livechat.window.show()
        : onConnected(getState),
    );
  };

export function loadZopim({
  assetPath,
  getState,
  justAcceptedCookies,
  locale,
  personalDetails,
  publicUrls,
  sharingDetails,
  translator,
  userInfos,
}: Args) {
  if (window.$zopim) {
    return;
  }
  // eslint-disable-next-line no-unused-expressions
  window.$zopim ||
    (function (d: any, s: any) {
      var z: any = (window.$zopim = function (c) {
          z._.push(c);
        }),
        $ = (z.s = d.createElement(s)),
        e = d.getElementsByTagName(s)[0];
      z.set = function (o) {
        z.set._.push(o);
      };
      z._ = [];
      // $FlowIgnore[prop-missing]
      z.set._ = [];
      $.defer = !0;
      $.setAttribute('charset', 'utf-8');
      $.src = 'https://v2.zopim.com/?DRL49mDICZUfknExghxjoOwO891X6ZN8';
      z.t = +new Date();
      if (e && e.parentNode) {
        e.parentNode.insertBefore($, e);
      }
    })(document, 'script');

  const languageMap = {
    'zh-hans': 'zh_CN',
    'zh-hant': 'zh_TW',
  };
  // $FlowFixMe[invalid-computed-prop]
  const language = locale in languageMap ? languageMap[locale] : locale;
  const isConnected = !!userInfos?.username;

  // Set user info.
  window.$zopim(
    onLoad({
      assetPath,
      getState,
      isConnected,
      justAcceptedCookies,
      language,
      personalDetails,
      publicUrls,
      sharingDetails,
      translator,
      userInfos,
    }),
  );
}
